// Three state machines — `selected`, `preprint`, `reviewer` — as transition tables.
// Illegal transitions throw (NFR-10). Exhaustively unit-tested (state-machine.test.ts).
import type { SelectedStatus, PreprintStatus, ReviewerStatus } from "./enums.js";

export type MachineName = "selected" | "preprint" | "reviewer";

export class IllegalTransitionError extends Error {
  readonly machine: MachineName;
  readonly from: string;
  readonly to: string;
  readonly httpStatus = 409;
  constructor(machine: MachineName, from: string, to: string) {
    super(`Illegal ${machine} transition: "${from}" → "${to}"`);
    this.name = "IllegalTransitionError";
    this.machine = machine;
    this.from = from;
    this.to = to;
  }
}

type TransitionTable<S extends string> = Readonly<Record<S, readonly S[]>>;

// ── 1. Selection lifecycle (Selected.Status) — forward, with a reject branch at
// each pitch gate and DIRECT AE-gated selection (July-2 FR-J4; the earlier
// `Proposed for Selection` two-step is removed):
//   To Pitch(Team)      → To Pitch(Editorial) | Team Rejected
//   To Pitch(Editorial) → Selected | Editorial Rejected
export const SELECTED_TRANSITIONS: TransitionTable<SelectedStatus> = {
  Unread: ["Taken"],
  Taken: ["To Pitch(Team)", "Unread"], // Unread = unclaim back to the discovery pool
  "To Pitch(Team)": ["To Pitch(Editorial)", "Team Rejected"],
  "Team Rejected": [],
  "To Pitch(Editorial)": ["Selected", "Editorial Rejected"],
  "Editorial Rejected": [],
  Selected: [],
  // Legacy SoftR pool statuses (imported from Team1_Preprints): once-rejected preprints go
  // back into circulation; twice-rejected are terminal.
  "Rejected Once": ["Taken"],
  "Rejected Twice": [],
};

// ── 2. Reviewer-pipeline lifecycle (Preprint Info ONLY.Preprint Status) ──────
// July-14 FR-P3: the flow is `DORMANT → ACTIVE → ON HOLD` (or → COMPLETE). An ACTIVE preprint
// NEVER reverts to DORMANT — activation is one-way (transcript: "It can't go from active to
// dormant"). ON HOLD ↔ ACTIVE still interchange (the FR-K10 auto-hold pauses/resumes). Any
// pre-invite state or INVITED → DISCARDED. The ONLY way to move an ACTIVE preprint back to
// DORMANT is the Central/Admin force-status override (FR-P6), which bypasses this table.
export const PREPRINT_TRANSITIONS: TransitionTable<PreprintStatus> = {
  ACTIVE: ["ON HOLD", "INVITED", "DISCARDED"], // FR-P3: no ACTIVE → DORMANT
  "ON HOLD": ["ACTIVE", "DORMANT", "INVITED", "DISCARDED"],
  DORMANT: ["ACTIVE", "ON HOLD", "INVITED", "DISCARDED"],
  INVITED: ["COMPLETE", "DISCARDED"],
  COMPLETE: [],
  DISCARDED: [],
};

// ── 3. Reviewer proposal lifecycle (Student Reviewer Inputs.Status) ──────────
// July-2 trimmed set (FR-J13): the COI hit, the Fixable→Fixed loop, same-institution
// backup handling, and the junior→senior Double Check path. Proposals are complete on
// submit (no `Incomplete`); a backup is promoted straight from `Invite Backup Unchecked`.
export const REVIEWER_TRANSITIONS: TransitionTable<ReviewerStatus> = {
  Unchecked: [
    "Invite",
    "Reject",
    "Fixable",
    "Invite Backup Unchecked",
    "Double Check",
    "COI Detected",
    "Student Reject",
  ],
  // A QC can always revert a non-terminal decision back to "Unchecked" (status dropdown).
  // Accept COI (student agrees with PROFI and withdraws) → Student Reject; Contest unlocks Part 2
  // without changing status until the AE decides.
  "COI Detected": ["Reject", "Fixable", "Unchecked", "Student Reject"],
  Fixable: ["Fixed", "Reject", "Student Reject", "Unchecked"],
  Fixed: ["Invite", "Reject", "Fixable", "Invite Backup Unchecked", "Double Check", "Unchecked"],
  "Double Check": ["Invite", "Reject", "Fixable", "Unchecked"],
  "Invite Backup Unchecked": ["Invite", "Reject", "Fixable", "Unchecked"],
  Invite: ["Invite Backup Unchecked", "Reject", "Unchecked"],
  Reject: [], // terminal
  "Student Reject": [], // terminal
};

const TABLES = {
  selected: SELECTED_TRANSITIONS,
  preprint: PREPRINT_TRANSITIONS,
  reviewer: REVIEWER_TRANSITIONS,
} as const;

function table(machine: MachineName): TransitionTable<string> {
  return TABLES[machine] as TransitionTable<string>;
}

/** True when `from → to` is a declared transition for the machine. */
export function canTransition(machine: MachineName, from: string, to: string): boolean {
  const next = table(machine)[from];
  return next != null && next.includes(to);
}

/** Throws IllegalTransitionError (HTTP 409) when the transition is not declared. */
export function assertTransition(machine: MachineName, from: string, to: string): void {
  if (!canTransition(machine, from, to)) {
    throw new IllegalTransitionError(machine, from, to);
  }
}

/** The states reachable from `from` in one step. */
export function nextStates(machine: MachineName, from: string): readonly string[] {
  return table(machine)[from] ?? [];
}

/** All declared states for a machine (table keys). */
export function statesOf(machine: MachineName): readonly string[] {
  return Object.keys(table(machine));
}
